import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Building2, Check, ChevronsUpDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useActiveTeam, useCanWriteCompanies } from "@/lib/team-context";
import { useActiveCompany } from "@/lib/company-context";
import { useCompanies } from "@/lib/api/companies.queries";
import { cn } from "@/lib/cn";

const COMPANY_PATH_RE = /^\/companies\/[^/]+(.*)$/;

/**
 * If the user is currently on a company-scoped page (anything matching
 * `/companies/<id>/...`), return the same view under a new company id.
 * Otherwise return null so the switcher just updates the active-company
 * context without a navigation.
 *
 * Without this, picking a different company in the dropdown updates state
 * but leaves the URL pointing at the old company — so the page (which reads
 * `companyId` from `Route.useParams()`) keeps showing the old company's data
 * and the user sees "nothing happen".
 */
function nextCompanyPath(currentPath: string, newCompanyId: string): string | null {
  const m = COMPANY_PATH_RE.exec(currentPath);
  if (!m) return null;
  return `/companies/${newCompanyId}${m[1] ?? ""}`;
}

const ENV_VARIANT: Record<string, "muted" | "success"> = {
  hab: "muted",
  prod: "success",
};

/**
 * Header slot rendered after TeamSwitcher. Three rendering modes:
 *   • no team yet         → null (the team switcher is the right CTA)
 *   • zero companies      → "Nueva empresa" CTA linking to /companies/new
 *                           (null for a viewer, who can't create one)
 *   • >= 1 companies       → dropdown with checkmark on the active company
 */
export function CompanySwitcher() {
  const { t } = useTranslation();
  const { activeTeam } = useActiveTeam();
  const canCreate = useCanWriteCompanies();
  const companiesQuery = useCompanies();
  const { activeCompany, setActiveCompanyId } = useActiveCompany();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleSelect = (companyId: string) => {
    setActiveCompanyId(companyId);
    const next = nextCompanyPath(pathname, companyId);
    if (next) navigate({ to: next });
  };

  if (!activeTeam) return null;

  if (companiesQuery.isPending) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="hidden gap-2 sm:inline-flex"
      >
        <span className="text-muted-foreground">
          {t("companies.switcher.loading")}
        </span>
      </Button>
    );
  }

  const companies = companiesQuery.data?.companies ?? [];

  if (companies.length === 0) {
    if (!canCreate) return null;
    return (
      <Button asChild variant="default" size="sm" className="hidden gap-2 sm:inline-flex">
        <Link to="/companies/new">
          <Plus className="h-3.5 w-3.5" />
          {t("companies.switcher.create_first")}
        </Link>
      </Button>
    );
  }

  const current = activeCompany ?? companies[0]!;
  const envVariant = ENV_VARIANT[current.dianRuntimeEnv] ?? "muted";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="hidden gap-2 sm:inline-flex"
          aria-label={t("companies.switcher.aria")}
        >
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="max-w-[160px] truncate">{current.legalName}</span>
          <Badge variant={envVariant} className="text-[10px] uppercase">
            {t(`companies.env.${current.dianRuntimeEnv}`)}
          </Badge>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {t("companies.switcher.label")}
        </DropdownMenuLabel>
        {companies.map((company) => {
          const isActive = company.id === current.id;
          const variant = ENV_VARIANT[company.dianRuntimeEnv] ?? "muted";
          return (
            <DropdownMenuItem
              key={company.id}
              onClick={() => handleSelect(company.id)}
              className="flex items-center gap-2"
            >
              <Check
                className={cn(
                  "h-4 w-4",
                  isActive ? "opacity-100" : "opacity-0",
                )}
              />
              <span className="flex-1 truncate">{company.legalName}</span>
              <Badge variant={variant} className="text-[10px] uppercase">
                {t(`companies.env.${company.dianRuntimeEnv}`)}
              </Badge>
            </DropdownMenuItem>
          );
        })}
        {canCreate ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/companies/new">
                <Plus className="h-4 w-4" />
                {t("companies.switcher.create_new")}
              </Link>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
